import type { ComponentType } from "react";
import { withBlockBoundary } from "./blockBoundary";
import { UnknownComponent, NoopComponent, PageWrapper, Body, AppBar, Footer } from "./layout";
import { TextChild } from "./textChild";
import { IconChild } from "./iconChild";
import { ImageChild } from "./imageChild";
import { Button } from "./button";
import { VideoChild, MapChild } from "./mediaChildren";
import { FormInput } from "./formInput";
import { FieldsGroup, CompanionsGroup, OverviewGroup, Checkboxes, CheckboxesGroup, SubmitGroup } from "./formGroups";
import { AppBarLogo, AppBarLinks, AppBarSocial, ContactSocial, AppBarSection } from "./appBar";
import { FooterLinks, FooterSocial, FooterContact, FooterCopyright, FooterSection } from "./footer";
import { Agenda, AgendaChild } from "./agenda";
import { Blog, BlogChild } from "./blog";
import { Speaker, SpeakerChild } from "./speaker";
import { Testimonials, TestimonialsChild } from "./testimonials";
import { Numbers, NumbersChild } from "./numbers";
import { FaQ, FaQChild } from "./faq";
import { Countdown, CountdownChild } from "./countdown";
import { Gallery } from "./gallery";
import { Hero } from "./hero";
import { TextSection } from "./textSection";
import { Image, Map, Video } from "./mediaSections";
import { Contact } from "./contact";
import { DataInput } from "./dataInput";
import { Register, RegisterChild, RegisterSuccessGroup, Login, LoginChild } from "./auth";
import { BookingGroup } from "./bookingGroup";
import { SessionGroup } from "./sessionGroup";
import { SlottingBooking } from "./slottingBooking";
import { SlottingAppointment } from "./slottingAppointment";
import { SlottingWaitlist } from "./slottingWaitlist";
import { SlottingAvailabilityCalendar } from "./slottingAvailability";
import { Embed } from "./embed";
import { Sponsors, SponsorsChild } from "./sponsors";

const noop = NoopComponent as ComponentType<Record<string, unknown>>;

const baseRuntimeResolver: Record<string, ComponentType<Record<string, unknown>>> = {
  Agenda,
  AppBar,
  // Backward-compatible aliases used by older serialized trees.
  AppBarChild: AppBar,
  AppBarSection,
  Contact,
  Countdown,
  Blog,
  VideoChild,
  FaQ,
  DataInput,
  Checkboxes,
  CheckboxesSettings: noop,
  Footer,
  Hero,
  MapChild,
  Button,
  ButtonChild: Button,
  Image,
  Text: TextSection,
  Gallery,
  FooterSection,
  Login,
  LoginSection: Login,
  FormInput,
  FormInputChild: FormInput,
  Map,
  PageWrapper,
  Register,
  RegisterSection: Register,
  Body,
  Numbers,
  Speaker,
  FaQChild,
  Testimonials,
  TextSection,
  Video,
  NumbersChild,
  BlogChild,
  TextChild,
  SpeakerChild,
  AppBarSettingsSection: noop,
  ImageChild,
  AgendaChild,
  IconChild,
  CountdownChild,
  TestimonialsChild,
  LoginChild,
  LoginChildSettings: noop,
  RegisterChild,
  RegisterChildSettings: noop,
  RegisterSuccessGroup,
  RegisterSuccessGroupSettings: noop,
  MapSettings: noop,
  DataInputSettings: noop,
  ButtonSettings: noop,
  LoginSettings: noop,

  Embed,
  EmbedSettings: noop,
  Sponsors,
  SponsorsChild,
  SponsorsSettings: noop,
  SponsorsChildSettings: noop,

  AgendaSettings: noop,
  AppBarSettings: noop,
  ContactSettings: noop,
  CountdownSettings: noop,
  RegisterSettings: noop,
  BlogSettings: noop,
  ImageSettings: noop,
  GallerySettings: noop,
  TextSettingsSection: noop,
  VideoSettings: noop,
  HeroSettings: noop,
  FaQSettings: noop,
  FormInputSettings: noop,
  FooterSettings: noop,
  FooterSettingsSection: noop,
  TextSettingsChildren: noop,
  BodySettings: noop,
  NumbersSettings: noop,
  SpeakerSettings: noop,
  IconChildSettings: noop,
  TestimonialsSettings: noop,
  AppBarLogo,
  AppBarLogoChild: AppBarLogo,
  FooterLogoChild: AppBarLogo,
  AppBarLogoSettings: noop,
  AppBarLinks,
  AppBarLinksChild: AppBarLinks,
  AppBarLinksSettings: noop,
  AppBarSocial,
  AppBarSocialChild: AppBarSocial,
  AppBarSocialSettings: noop,
  ContactSocial,
  ContactSocialChild: ContactSocial,
  ContactSocialSettings: noop,
  FooterContact,
  FooterContactChild: FooterContact,
  FooterContactSettings: noop,
  FooterSocial,
  FooterSocialChild: FooterSocial,
  FooterSocialSettings: noop,
  FooterLinks,
  FooterLinksChild: FooterLinks,
  FooterLinksSettings: noop,
  FooterCopyright,
  FooterCopyrightChild: FooterCopyright,
  FooterCopyrightSettings: noop,
  FieldsGroup,
  FieldsGroupSettings: noop,
  CompanionsGroup,
  CompanionsGroupSettings: noop,
  OverviewGroup,
  OverviewGroupSettings: noop,
  CheckboxesGroup,
  CheckboxesGroupSettings: noop,
  SubmitGroup,
  SubmitGroupSettings: noop,
  BookingGroup,
  BookingGroupSettings: noop,
  SessionGroup,
  SessionGroupSettings: noop,

  SlottingBookingWidget: SlottingBooking,
  SlottingBookingWidgetSettings: noop,
  SlottingAvailabilityWidget: SlottingAvailabilityCalendar,
  SlottingAvailabilityWidgetSettings: noop,
  SlottingAppointmentWidget: SlottingAppointment,
  SlottingAppointmentWidgetSettings: noop,
  SlottingWaitlistWidget: SlottingWaitlist,
  SlottingWaitlistWidgetSettings: noop,

  UnknownComponent,
};

// FIXED BD-6 (H25): wrap every runtime block in its own error boundary so one
// corrupt node can't AppCrash the whole published site (see blockBoundary.tsx).
export const runtimeResolver: Record<string, ComponentType<Record<string, unknown>>> =
  Object.fromEntries(
    Object.entries(baseRuntimeResolver).map(([name, Comp]) => [name, withBlockBoundary(Comp)]),
  );

export const knownComponentNames = new Set<string>(Object.keys(runtimeResolver));
